/**
 * Proactive monitor store — Track A3.
 *
 * Backs the /watch page. The api-shim's worker periodically snapshots a
 * watchlist of sites and raises a regression event (with an auto-run AI
 * diagnosis attached) when a site degrades.
 */
import { create } from "zustand";
import { get as apiGet, post as apiPost, del as apiDel, request, ApiError } from "@/api/client";

export type WatchSeverity = "severe" | "warning";

export interface WatchedSite {
  siteSlug: string;
  siteName: string | null;
  networkId: string | null;
  addedBy: string;
  addedAt: string;
  enabled: boolean;
  vendor: "meraki" | "aruba" | "cli";
}

/** One device that went online → down between watch ticks. */
export interface WatchRegression {
  serial: string;
  name: string | null;
  model: string | null;
  deviceClass: string | null;
  from: string;
  to: string;
}

export interface WatchEvent {
  id: string;
  detectedAt: string;
  siteSlug: string;
  siteName: string | null;
  severity: WatchSeverity;
  summary: string;
  detail: {
    headline?: string;
    applianceDown?: boolean;
    regressions?: WatchRegression[];
    verdict?: string | null;
    vendor?: string | null;
  } | null;
  diagnosisId: string | null;
  ackedAt: string | null;
}

function errMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const detail =
      err.data && typeof err.data === "object" && "detail" in err.data
        ? String((err.data as { detail: unknown }).detail)
        : null;
    return detail ?? `API error ${err.status}`;
  }
  return err instanceof Error ? err.message : "unknown error";
}

interface State {
  sites: WatchedSite[];
  events: WatchEvent[];
  status: "idle" | "loading" | "ready" | "error";
  error: string | null;
  running: boolean;

  loadAll: () => Promise<void>;
  loadEvents: () => Promise<void>;
  addSite: (slug: string, vendor?: "meraki" | "aruba" | "cli") => Promise<boolean>;
  removeSite: (slug: string) => Promise<void>;
  setEnabled: (slug: string, enabled: boolean) => Promise<void>;
  ackEvent: (id: string) => Promise<void>;
  runNow: () => Promise<{ checked: number; events: number } | null>;
}

export const useWatch = create<State>((set, get) => ({
  sites: [],
  events: [],
  status: "idle",
  error: null,
  running: false,

  loadAll: async () => {
    set({ status: "loading", error: null });
    try {
      const [sites, events] = await Promise.all([
        apiGet<WatchedSite[]>("/watch/sites"),
        apiGet<WatchEvent[]>("/watch/events", { limit: 100 }),
      ]);
      set({ sites, events, status: "ready" });
    } catch (err) {
      set({ status: "error", error: errMessage(err) });
    }
  },

  loadEvents: async () => {
    try {
      const events = await apiGet<WatchEvent[]>("/watch/events", { limit: 100 });
      set({ events });
    } catch {
      // Non-fatal — keep whatever is cached.
    }
  },

  addSite: async (slug, vendor = "meraki") => {
    try {
      await apiPost("/watch/sites", { slug, vendor });
      await get().loadAll();
      return true;
    } catch (err) {
      set({ error: errMessage(err) });
      return false;
    }
  },

  removeSite: async (slug) => {
    try {
      await apiDel(`/watch/sites/${encodeURIComponent(slug)}`);
      set((s) => ({ sites: s.sites.filter((x) => x.siteSlug !== slug) }));
    } catch (err) {
      set({ error: errMessage(err) });
    }
  },

  setEnabled: async (slug, enabled) => {
    // Optimistic — reconcile on failure.
    set((s) => ({
      sites: s.sites.map((x) => (x.siteSlug === slug ? { ...x, enabled } : x)),
    }));
    try {
      await request(`/watch/sites/${encodeURIComponent(slug)}`, {
        method: "PATCH",
        body: { enabled },
      });
    } catch (err) {
      set({ error: errMessage(err) });
      void get().loadAll();
    }
  },

  ackEvent: async (id) => {
    set((s) => ({
      events: s.events.map((e) =>
        e.id === id ? { ...e, ackedAt: new Date().toISOString() } : e,
      ),
    }));
    try {
      await apiPost(`/watch/events/${encodeURIComponent(id)}/ack`);
    } catch {
      // Silent — next loadEvents() reconciles.
    }
  },

  runNow: async () => {
    if (get().running) return null;
    set({ running: true });
    try {
      const res = await apiPost<{ checked: number; events: number }>("/watch/run-now");
      await get().loadEvents();
      return res;
    } catch (err) {
      set({ error: errMessage(err) });
      return null;
    } finally {
      set({ running: false });
    }
  },
}));
